import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { AdminUser, MeResponse } from '@/types/auth'
import { getMe, logout as logoutApi } from '@/api/auth/auth'

interface AuthState {
  token: string | null
  user: AdminUser | null
  meInfo: MeResponse | null
  isAuthenticated: boolean
  _hasHydrated: boolean
}

interface AuthActions {
  login(token: string, user: AdminUser): void
  logout(): Promise<void>
  clearSession(): void
  fetchMe(): Promise<void>
  rehydrate(): void
  setHasHydrated(value: boolean): void
}

type AuthStore = AuthState & AuthActions

const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      token: null,
      user: null,
      meInfo: null,
      isAuthenticated: false,
      _hasHydrated: false,

      login(token: string, user: AdminUser) {
        set({ token, user, isAuthenticated: true })
      },

      async logout() {
        try {
          await logoutApi()
        } catch {
          // 接口失败时仍清除本地状态，确保用户能退出
        } finally {
          set({ token: null, user: null, meInfo: null, isAuthenticated: false })
        }
      },

      // 仅清空本地会话状态，不调用 logout 接口（会话已失效，调用只会再次 401）。
      // 供 401 拦截器在会话过期时使用，state 变更会触发 RouteGuard 跳转登录页。
      clearSession() {
        set({ token: null, user: null, meInfo: null, isAuthenticated: false })
      },

      async fetchMe() {
        try {
          const meInfo = await getMe()
          set({ meInfo })
        } catch (err) {
          console.warn('[fetchMe] failed:', err)
        }
      },

      rehydrate() {
        const stored = localStorage.getItem('auth-storage')
        if (stored) {
          try {
            const parsed = JSON.parse(stored)
            const token = parsed?.state?.token ?? null
            set({ isAuthenticated: !!token })
          } catch {
            set({ isAuthenticated: false })
          }
        } else {
          set({ isAuthenticated: false })
        }
      },

      setHasHydrated(value: boolean) {
        set({ _hasHydrated: value })
      },
    }),
    {
      name: 'auth-storage',
      // Persist token and user; isAuthenticated is derived on rehydration
      partialize: (state) => ({ token: state.token, user: state.user }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.isAuthenticated = !!state.token
          state._hasHydrated = true
        }
      },
    }
  )
)

export default useAuthStore
